import React, { useEffect, useRef, useState } from "react";
import { HeartHandshake, CheckCircle2, Phone, ShieldAlert } from "lucide-react";
import { useClara } from "../contexts/ClaraContext";
import { useData } from "../contexts/DataContext";

const RESPONSE_SECONDS = 30;

/**
 * InactivityCheck — when `open` becomes true, Clara prompts "Are you okay, Maria?"
 * 30-second countdown begins; if user taps "I'm okay", the modal dismisses and
 * Clara confirms. If countdown reaches 0, Clara escalates to primary + alert
 * contacts and shows an "Alert sent" confirmation for 6 seconds.
 */
export const InactivityCheck = ({ open, onDismiss }) => {
    const { speak } = useClara();
    const { primaryContact, alertContact } = useData();
    const [phase, setPhase] = useState("prompt"); // prompt | escalated
    const [remaining, setRemaining] = useState(RESPONSE_SECONDS);
    const intervalRef = useRef(null);
    const dismissTimerRef = useRef(null);

    const cleanup = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
        intervalRef.current = null;
        dismissTimerRef.current = null;
    };

    // When opening, reset state and start countdown
    useEffect(() => {
        if (!open) {
            cleanup();
            setPhase("prompt");
            setRemaining(RESPONSE_SECONDS);
            return undefined;
        }
        setPhase("prompt");
        setRemaining(RESPONSE_SECONDS);
        speak(
            `Maria, I haven't heard from you in a while. Are you okay? Tap I'm okay within ${RESPONSE_SECONDS} seconds, or I'll alert ${primaryContact?.name || "your contact"}.`,
        );
        intervalRef.current = setInterval(() => {
            setRemaining((s) => (s > 0 ? s - 1 : 0));
        }, 1000);
        return cleanup;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Escalation when countdown reaches 0 — runs as effect, not in updater
    useEffect(() => {
        if (open && phase === "prompt" && remaining === 0) {
            cleanup();
            setPhase("escalated");
            speak(
                `I haven't heard back from Maria. Sending an alert to ${primaryContact?.name || "Tom"} and sharing her location with ${alertContact?.name || "Jenny"} now.`,
            );
            dismissTimerRef.current = setTimeout(() => {
                onDismiss?.();
            }, 6000);
        }
    }, [open, phase, remaining, primaryContact, alertContact, speak, onDismiss]);

    const escalateNow = () => {
        cleanup();
        setRemaining(0);
        // Effect above will switch phase to 'escalated' and fire speak()
    };

    if (!open) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Wellness check"
            data-testid="inactivity-check"
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-[#1A1A2E]/60 backdrop-blur-sm"
        >
            {phase === "prompt" ? (
                <div
                    className="w-full max-w-[448px] bg-white rounded-3xl border-2 border-[#B8860B] p-6 shadow-2xl fade-in-up"
                    data-testid="inactivity-prompt"
                >
                    <div className="flex items-start gap-3">
                        <span
                            aria-hidden="true"
                            className="relative shrink-0 w-14 h-14 rounded-2xl bg-[#B8860B] flex items-center justify-center"
                        >
                            <span className="absolute inset-0 rounded-2xl bg-[#B8860B]/50 pulse-ring" />
                            <HeartHandshake
                                className="relative w-7 h-7 text-[#1A1A2E]"
                                strokeWidth={2.3}
                            />
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#B8860B] mb-1">
                                Wellness check
                            </p>
                            <h2 className="font-display text-2xl font-bold text-[#1A1A2E] leading-tight">
                                Are you okay, Maria?
                            </h2>
                            <p className="text-sm text-[#5A5A6E] mt-1 leading-snug">
                                I'll alert{" "}
                                <span className="font-semibold text-[#1A1A2E]">
                                    {primaryContact?.name || "Tom"}
                                </span>{" "}
                                in{" "}
                                <span
                                    className="font-bold tabular-nums text-[#A31621]"
                                    data-testid="inactivity-countdown"
                                >
                                    {remaining}s
                                </span>{" "}
                                if I don't hear from you.
                            </p>
                        </div>
                    </div>
                    <div className="mt-5 space-y-3">
                        <button
                            type="button"
                            onClick={() => {
                                cleanup();
                                speak("Glad you're okay, Maria.");
                                onDismiss?.();
                            }}
                            data-testid="inactivity-okay-btn"
                            className="w-full min-h-[56px] rounded-2xl bg-[#0A5C36] hover:bg-[#0a4d2d] text-white text-lg font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                        >
                            <CheckCircle2 className="w-5 h-5" aria-hidden="true" />
                            I'm okay
                        </button>
                        <button
                            type="button"
                            onClick={escalateNow}
                            data-testid="inactivity-need-help-btn"
                            className="w-full min-h-[52px] rounded-2xl bg-[#A31621] hover:bg-[#7c101a] text-white text-base font-bold flex items-center justify-center gap-2 transition-colors"
                        >
                            <ShieldAlert className="w-5 h-5" aria-hidden="true" />
                            I need help — alert now
                        </button>
                    </div>
                </div>
            ) : (
                <div
                    className="w-full max-w-[448px] bg-white rounded-3xl border-2 border-[#0A5C36] p-6 shadow-2xl fade-in-up"
                    data-testid="inactivity-escalated"
                    role="status"
                    aria-live="assertive"
                >
                    <div className="flex items-start gap-3">
                        <span
                            aria-hidden="true"
                            className="shrink-0 w-14 h-14 rounded-2xl bg-[#0A5C36] text-white flex items-center justify-center"
                        >
                            <Phone className="w-7 h-7" />
                        </span>
                        <div>
                            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#0A5C36] mb-1">
                                Alert sent
                            </p>
                            <h2 className="font-display text-2xl font-bold text-[#1A1A2E] leading-tight">
                                Calling {primaryContact?.name || "Tom"}
                            </h2>
                            <p className="text-sm text-[#1A1A2E] mt-1 leading-snug">
                                Sharing your location with{" "}
                                <span className="font-semibold">
                                    {alertContact?.name || "Jenny"}
                                </span>
                                . Help is on the way.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InactivityCheck;
